import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { lumi } from '../lib/lumi';
import { toast } from 'react-hot-toast';
import {LayoutDashboard, PlusCircle, History, DollarSign, FileText, CreditCard, Smartphone, X, Check} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch service requests
        // @ts-ignore
        const { list: requestsList } = await lumi.entities.ServiceRequests.list({
          filter: { userId: user?.userId },
          sort: { createdAt: -1 }
        });
        setRequests(requestsList);

        // Fetch invoices
        // @ts-ignore
        const { list: invoicesList } = await lumi.entities.Invoices.list({
          filter: { userId: user?.userId },
          sort: { createdAt: -1 }
        });
        setInvoices(invoicesList);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handlePayInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setPaymentMethod('');
  };

  const handleRejectInvoice = async (invoiceId: string) => {
    const loadingToast = toast.loading(t('invoice.rejecting'));
    try {
      // @ts-ignore
      await lumi.entities.Invoices.update(invoiceId, {
        status: 'rejected',
        updatedAt: new Date().toISOString()
      });
      
      setInvoices(prev => prev.map(inv => 
        inv._id === invoiceId ? { ...inv, status: 'rejected' } : inv
      ));
      
      toast.success(t('invoice.rejected'), { id: loadingToast });
    } catch (error) {
      console.error('Failed to reject invoice:', error);
      toast.error(t('invoice.rejectFailed'), { id: loadingToast });
    }
  };

  const processPayment = async () => {
    if (!paymentMethod) {
      toast.error(t('invoice.selectPaymentMethod'));
      return;
    }

    setIsProcessing(true);
    const loadingToast = toast.loading(t('invoice.processing'));

    try {
      if (paymentMethod === 'stripe') {
        // Stripe payment flow
        toast.info('Stripe integration coming soon', { id: loadingToast });
      } else if (paymentMethod === 'paypal') {
        // PayPal payment flow
        toast.info('PayPal integration coming soon', { id: loadingToast });
      } else {
        // Mobile Money (MTN, Vodafone, AirtelTigo)
        const mobileMoneyInstructions = `
          <div style="font-family: sans-serif;">
            <h2 style="color: #0f172a;">Mobile Money Payment Instructions</h2>
            <p><strong>Amount:</strong> $${selectedInvoice.amount}</p>
            <p><strong>Payment Number:</strong> 0277549999 (AirtelTigo)</p>
            <ol style="line-height: 1.8;">
              <li>Dial *110# (MTN), *110# (Vodafone), or *110# (AirtelTigo)</li>
              <li>Select "Send Money"</li>
              <li>Enter: <strong>0277549999</strong></li>
              <li>Enter amount: <strong>${selectedInvoice.amount} USD</strong></li>
              <li>Confirm the transaction</li>
              <li>Send the transaction reference to us via email</li>
            </ol>
            <p style="color: #64748b; font-size: 14px;">Our team will verify your payment and update your invoice status within 24 hours.</p>
          </div>
        `;

        // Send payment instructions email
        await lumi.tools.email.send({
          to: user?.email || '',
          subject: `Payment Instructions - Invoice #${selectedInvoice._id.slice(-6)}`,
          html: mobileMoneyInstructions
        });

        // Update invoice status to pending verification
        // @ts-ignore
        await lumi.entities.Invoices.update(selectedInvoice._id, {
          paymentMethod,
          status: 'pending',
          updatedAt: new Date().toISOString()
        });

        setInvoices(prev => prev.map(inv => 
          inv._id === selectedInvoice._id 
            ? { ...inv, status: 'pending', paymentMethod } 
            : inv
        ));

        toast.success(t('invoice.instructionsSent'), { id: loadingToast });
        setSelectedInvoice(null);
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(t('invoice.paymentFailed'), { id: loadingToast });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'paid': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const pendingInvoices = invoices.filter(inv => inv.status === 'pending');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{t('dashboard.title') || 'Dashboard'}</h1>
              <p className="text-slate-400 mt-1">{t('dashboard.welcome') || 'Welcome back'}, {user?.userName || user?.email}</p>
            </div>
            <button 
              onClick={() => navigate('/request-service')}
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-lg shadow-sky-900/20"
            >
              <PlusCircle className="h-5 w-5" />
              {t('dashboard.newRequest') || 'New Service Request'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                <History className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.totalRequests') || 'Total Requests'}</p>
                <p className="text-2xl font-bold text-slate-900">{requests.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.pendingInvoices') || 'Pending Invoices'}</p>
                <p className="text-2xl font-bold text-slate-900">{pendingInvoices.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 text-green-600 rounded-lg">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('dashboard.paidInvoices') || 'Paid Invoices'}</p>
                <p className="text-2xl font-bold text-slate-900">{paidInvoices.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900">{t('dashboard.invoices') || 'Invoices'}</h2>
          </div>
          
          {invoices.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                <FileText className="h-8 w-8" />
              </div>
              <h3 className="text-slate-900 font-medium mb-1">{t('dashboard.noInvoices') || 'No invoices yet'}</h3>
              <p className="text-slate-500 text-sm">{t('dashboard.invoicesAppear') || 'Invoices will appear here after admin approval'}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {invoices.map((invoice) => (
                <div key={invoice._id} className="p-6 hover:bg-slate-50 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900">
                          {t('dashboard.invoice') || 'Invoice'} #{invoice._id.slice(-6)}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">
                        {t('dashboard.services') || 'Services'}: {invoice.serviceTypes?.join(', ')}
                      </p>
                      <p className="text-2xl font-bold text-sky-600">${invoice.amount}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    {invoice.status === 'pending' && (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handlePayInvoice(invoice)}
                          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors font-medium"
                        >
                          <DollarSign className="h-4 w-4" />
                          {t('dashboard.pay') || 'Pay'}
                        </button>
                        <button
                          onClick={() => handleRejectInvoice(invoice._id)}
                          className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
                        >
                          <X className="h-4 w-4" />
                          {t('dashboard.reject') || 'Reject'}
                        </button>
                      </div>
                    )}

                    {invoice.status === 'paid' && (
                      <div className="flex items-center gap-2 text-green-600">
                        <Check className="h-5 w-5" />
                        <span className="font-medium">{t('dashboard.paid') || 'Paid'}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Requests */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-bold text-slate-900">{t('dashboard.recentRequests') || 'Recent Requests'}</h2>
          </div>
          
          {requests.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                <LayoutDashboard className="h-8 w-8" />
              </div>
              <h3 className="text-slate-900 font-medium mb-1">{t('dashboard.noRequests') || 'No requests yet'}</h3>
              <p className="text-slate-500 text-sm mb-6">{t('dashboard.getStarted') || 'Get started by creating your first service request'}</p>
              <button 
                onClick={() => navigate('/request-service')}
                className="text-sky-600 font-medium hover:text-sky-500"
              >
                {t('dashboard.createRequest') || 'Create Request'} &rarr;
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {requests.map((request) => (
                <div key={request._id} className="p-6 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">
                        {request.serviceTypes?.join(', ')}
                      </h3>
                      <p className="text-sm text-slate-600">{request.city}, {request.country}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900">{t('invoice.selectPayment') || 'Select Payment Method'}</h3>
              <button onClick={() => setSelectedInvoice(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-slate-600 mb-1">{t('invoice.amount') || 'Amount'}</p>
              <p className="text-3xl font-bold text-sky-600">${selectedInvoice.amount}</p>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => setPaymentMethod('stripe')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'stripe' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <CreditCard className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-900">{t('payment.creditCard') || 'Credit/Debit Card (Stripe)'}</span>
              </button>

              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'paypal' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <DollarSign className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-900">PayPal</span>
              </button>

              <button
                onClick={() => setPaymentMethod('mtn')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'mtn' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Smartphone className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-900">{t('payment.mtn') || 'MTN Mobile Money'}</span>
              </button>

              <button
                onClick={() => setPaymentMethod('vodafone')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'vodafone' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Smartphone className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-900">{t('payment.vodafone') || 'Vodafone Cash'}</span>
              </button>

              <button
                onClick={() => setPaymentMethod('airteltigo')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  paymentMethod === 'airteltigo' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Smartphone className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-900">{t('payment.airteltigo') || 'AirtelTigo Money'}</span>
              </button>
            </div>

            <button
              onClick={processPayment}
              disabled={!paymentMethod || isProcessing}
              className="w-full bg-sky-600 text-white py-3 rounded-lg font-semibold hover:bg-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (t('invoice.processing') || 'Processing...') : (t('invoice.proceed') || 'Proceed to Payment')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;